<template>
  <div class="page-container recruitment-list-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ t('pages.recruitment_list_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ t('menu.home') }}</router-link> /
          <span>{{ t('pages.recruitment_list_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">

        <!-- 左侧：招聘列表 -->
        <el-col :span="24" :xs="24">
          <!-- 搜索框区域 -->
          <div class="search-wrapper">
            <el-input
              v-model="searchKeyword"
              :placeholder="t('recruitment.enter_keyword')"
              :prefix-icon="Search"
              @keyup.enter="handleSearch"
              clearable
            />
            <!-- 按钮容器 -->
            <div class="button-row">
              <el-button type="primary" @click="handleSearch" class="search-btn">
                {{ t('common.search') }}
              </el-button>
              <el-button
                v-if="searchKeyword"
                type="text"
                @click="resetSearch"
                class="reset-btn"
              >
                {{ t('common.reset') }}
              </el-button>
            </div>
          </div>
          <div class="list-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else-if="recruitmentList.length === 0" class="empty-data">
              <el-empty :description="t('recruitment.no_recruitment_data')" />
            </div>
            <div v-else>
              <div v-for="item in recruitmentList" :key="item.id" class="recruitment-item">
                <div class="item-icon">
                  <el-icon><UserFilled /></el-icon>
                </div>
                <div class="item-content">
                  <router-link :to="`/recruitment/${item.id}`">
                    <h3>{{ item.page_name }}</h3>
                  </router-link>
                  <div class="meta">
                    <span class="page-type" v-if="item.page_type !== 1">
                      {{ t(`recruitment.recruitment_type_${item.page_type}`) }}
                    </span>
                    <span class="sort-tag" v-if="item.sort === 99">
                      {{ t('recruitment.top') }}
                    </span>
                    <span class="time">{{ t('recruitment.create_time') }}: {{ formatTime(item.create_time) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页：适配 count/current 格式 -->
          <div v-if="!loading && totalPages > 1" class="pagination-wrapper">
            <el-pagination
              background
              size="small"
              layout="prev, pager, next"
              :total="totalCount"
              :page-size="pageSize"
              :current-page="currentPage"
              :page-count="totalPages"
              @current-change="handlePageChange"
            />
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { UserFilled, Search } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { getRecruitmentList } from '@/assets/api/website';

// -------------------------- 类型定义 --------------------------
/** 招聘列表项类型 */
interface RecruitmentListItem {
  id: number;
  page_type: number;
  page_name: string;
  sort: number;
  status: number;
  create_time: string | Date;
}

/** 招聘列表接口返回类型 */
interface RecruitmentListResponse {
  list: RecruitmentListItem[];
  count: number; // 总页数
  current: number; // 当前页码
}

// -------------------------- 状态管理 --------------------------
const { t, locale } = useI18n();

const loading = ref(true);
const typeLoading = ref(false); // 页面类型筛选加载状态

// 招聘列表数据
const recruitmentList = ref<RecruitmentListItem[]>([]);
// 分页相关
const pageSize = ref(10);
const currentPage = ref(1);
const totalPages = ref(0); // 总页数
const totalCount = ref(0); // 总条数（估算值）
// 筛选条件
const currentPageType = ref<number | null>(null); // 页面类型筛选
const searchKeyword = ref('');

// -------------------------- 方法定义 --------------------------
/** 格式化时间 */
const formatTime = (time: string | Date) => {
  if (!time) return '';
  try {
    return format(new Date(time), 'yyyy-MM-dd');
  } catch (e) {
    return time.toString();
  }
};

/** 获取招聘列表 */
const fetchRecruitmentList = async () => {
  loading.value = true;
  try {
    const params: any = {
      lang: locale.value,
      page: currentPage.value,
      size: pageSize.value
    };

    // 页面类型筛选（预留）
    if (currentPageType.value !== null) {
      params.page_type = currentPageType.value;
    }

    // 关键词搜索
    if (searchKeyword.value.trim()) {
      params.keyword = searchKeyword.value.trim();
    }

    const res = await getRecruitmentList(params) as RecruitmentListResponse;
    // console.log('招聘列表数据:', res);

    recruitmentList.value = res.list;
    totalPages.value = res.count;
    currentPage.value = res.current;

    // 计算总条数（用于分页组件显示）
    totalCount.value = totalPages.value * pageSize.value;

  } catch (error) {
    console.error('获取招聘列表失败:', error);
    recruitmentList.value = [];
    totalPages.value = 0;
    totalCount.value = 0;
    ElMessage.error(t('common.fetch_data_error'));
  } finally {
    loading.value = false;
  }
};

/** 分页切换 */
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchRecruitmentList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/** 页面类型筛选 */
const selectPageType = (pageType: number | null) => {
  currentPageType.value = pageType;
  currentPage.value = 1; // 切换类型重置页码
  fetchRecruitmentList();
};

/** 处理搜索 */
const handleSearch = () => {
  currentPage.value = 1;
  fetchRecruitmentList();
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/** 重置搜索 */
const resetSearch = () => {
  searchKeyword.value = '';
  currentPage.value = 1;
  fetchRecruitmentList();
};

// -------------------------- 监听与初始化 --------------------------
// 监听语言变化，重新加载数据
watch(
  () => locale.value,
  () => {
    fetchRecruitmentList();
  },
  { immediate: true }
);

// 初始化时加载数据
onMounted(() => {
  fetchRecruitmentList();
});
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.recruitment-list-page {
  .page-header {
    padding: 100px 0 40px;
    background:
      linear-gradient(90deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%),
      url('@/assets/img/header.jpg') center center / cover no-repeat;
    position: relative;
    border-bottom: 1px solid rgba($primary-color, 0.1);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: inherit;
      filter: blur(2px);
      z-index: -1;
    }

    h1 {
      font-family: $font-serif;
      font-size: 2.5rem;
      font-weight: 700;
      color: $primary-color-2;
      margin-bottom: 20px;
      letter-spacing: 5px;
      text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
      position: relative;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;
      position: relative;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
  }

  // 搜索框样式
  .search-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .el-input {
      flex: 1;
      max-width: 400px;
      border: 1px solid $primary-color;
    }

    :deep(.el-button) {
      padding: 10px 20px;
    }

    .search-btn {
      white-space: nowrap;
    }

    .reset-btn {
      color: #999;
      border: 1px solid $primary-color;

      &:hover {
        color: $primary-color;
      }
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: stretch;
      padding: 10px;

      .el-input {
        width: 100%;
        max-width: none;
        margin-bottom: 10px;
      }

      .button-row {
        display: flex;
        gap: 10px;
      }

      .search-btn, .reset-btn {
        flex: 1;
        min-width: auto;
      }
    }
  }

  .list-wrapper {
    min-height: 500px;
  }

  .recruitment-item {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    padding: 15px;
    border-bottom: 1px dashed #ddd;
    transition: background-color 0.3s, transform 0.3s;
    border-radius: 8px;

    &:hover {
      background-color: #f9f9f9;
      transform: translateX(5px);
    }

    .item-icon {
      flex-shrink: 0;
      font-size: 24px;
      color: $primary-color;
      margin-right: 15px;
    }

    .item-content {
      flex-grow: 1;

      h3 {
        font-size: 18px;
        margin: 0 0 8px 0;
        color: #333;
        transition: color 0.3s;
        letter-spacing: 1px;

        &:hover {
          color: $primary-color;
        }
      }

      .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        font-size: 13px;
        color: #999;

        .page-type {
          color: $primary-color;
          font-weight: bold;
          background: rgba($primary-color, 0.1);
          padding: 2px 8px;
          border-radius: 4px;
        }

        .sort-tag {
          color: #e6a23c;
          font-weight: bold;
          background: rgba(230, 162, 60, 0.1);
          padding: 2px 8px;
          border-radius: 4px;
        }

        .time {
          display: flex;
          align-items: center;

          &::before {
            content: "📅";
            margin-right: 4px;
          }
        }
      }
    }
  }

  .pagination-wrapper {
    text-align: center;
    margin-top: 30px;
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      margin-bottom: 30px;

      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            display: block;
            padding: 5px 0;
            transition: color 0.3s, padding-left 0.3s;

            &:hover {
              color: $primary-color;
              padding-left: 8px;
            }
          }

          &.active a {
            color: $primary-color;
            font-weight: bold;
            border-left: 3px solid $primary-color;
            padding-left: 8px;
          }
        }
      }
    }
  }

  // 响应式适配
  @media (max-width: 768px) {
    .page-content {
      padding: 20px;
    }

    .recruitment-item {
      padding: 12px;
      flex-wrap: wrap;

      .item-icon {
        font-size: 20px;
        margin-right: 12px;
      }

      .item-content {
        width: 100%;
        margin-right: 0;
        margin-bottom: 10px;

        h3 {
          font-size: 16px;
        }

        .meta {
          flex-direction: column;
          gap: 5px;
        }
      }
    }

    .sidebar-col {
      margin-top: 30px;
    }
  }
}
</style>